import { useContext } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { useLocation } from '@reach/router';
import { ResourceSetContext } from '../context/ResourceSetContext';
import { mapperWithFunction } from '../common/utils';
import { generateSchema } from '../utils/schemaUtils';

const STRUCTURED_DATA_MAP = {
  title: ({ menuLabel, headline }) => {
    return headline || menuLabel;
  },
  menuLabel: ({ menuLabel, headline }) => {
    return menuLabel || headline;
  },
  description: ['description', 'description'],
  image: ['image', 'fluid', 'src'],
  altText: ['altText'],
  meta: ({ meta }) => {
    return meta ? JSON.parse(meta.internal.content) : {};
  },
  noIndex: ['noIndex'],
  noFollow: ['noFollow'],
};

const useSeo = (data, FALLBACK_DATA_MAP, templateOrPageContext) => {
  const context = useContext(ResourceSetContext);
  const globalRS = context.getResourcesForComponent('global');
  const location = useLocation();
  const pathname = location?.pathname || '/';
  const search = location?.search || '';

  // Determine if we received a template string or pageContext object
  const template = typeof templateOrPageContext === 'string' ? templateOrPageContext : null;
  const pageContext = typeof templateOrPageContext === 'object' ? templateOrPageContext : {};

  const getCurrentPage = () => {
    // Prioritize SSR pageContext over client-side query params
    if (pageContext.currentPage) {
      return pageContext.currentPage;
    }
    // Fallback to query params for client-side navigation
    const urlParams = new URLSearchParams(search);
    const page = urlParams.get('page');
    return page ? parseInt(page) : 1;
  };

  const currentPage = getCurrentPage();
  const { site } = useStaticQuery(query);

  let structuredData = {};
  const fallbackData = mapperWithFunction(data, FALLBACK_DATA_MAP);

  if (data?.structuredData) {
    structuredData = mapperWithFunction(
      data.structuredData,
      STRUCTURED_DATA_MAP,
    );
  }

  const image = structuredData.image || fallbackData.image;

  const resolveBasePath = () => {
    if (typeof pageContext.basePath === 'string') {
      if (pageContext.basePath === '') return '/';
      return pageContext.basePath;
    }
    return pathname || '/';
  };

  const ensureLeadingSlash = value => {
    if (!value) return '/';
    return value.startsWith('/') ? value : `/${value}`;
  };

  const ensureTrailingSlash = value => {
    if (value === '/') return '/';
    return value.endsWith('/') ? value : `${value}/`;
  };

  const basePathRaw = ensureLeadingSlash(resolveBasePath());
  const basePathClean = ensureTrailingSlash(
    basePathRaw.replace(/\/page\/\d+\/?$/, '/')
  );

  const pagePath =
    currentPage > 1
      ? `${basePathClean}page/${currentPage}/`
      : basePathClean;

  const siteBaseUrl = site.siteMetadata.siteUrl || globalRS.websiteURL || '';
  const siteBaseClean = siteBaseUrl.replace(/\/+$/, '');
  const absoluteUrl =
    pagePath === '/'
      ? `${siteBaseClean}/`
      : `${siteBaseClean}${pagePath}`;

  let url = absoluteUrl;
  let canonical = absoluteUrl;

  let title =
    structuredData.title || fallbackData.title || fallbackData.headline;
  if (title && !title.endsWith('Singita'))
    title += ` | ${globalRS.websiteName}`;

  const seoData = {
    ...fallbackData,
    ...structuredData,
    url: url,
    origin: site.siteMetadata.siteUrl || globalRS.websiteURL,
    sdTitle: structuredData.title,
    title: title,
    menuLabel:
      structuredData.menuLabel || fallbackData.title || fallbackData.headline,
    description:
      structuredData.description ||
      fallbackData.description,
    image: image ? `${image.replace('./img', '/img')}` : globalRS.image,
    altText: structuredData.altText || fallbackData.altText || globalRS.altText,
    breadcrumb: fallbackData.breadcrumb || pathname.split('/')[1],
    websiteName: globalRS.websiteName,
    logo: globalRS.logo,
    telephone: globalRS.telephone,
    email: globalRS.email,
    streetAddress: globalRS.streetAddress,
    addressLocality: globalRS.addressLocality,
    addressRegion: globalRS.addressRegion,
    postalCode: globalRS.postalCode,
    addressCountry: globalRS.addressCountry,
  };

  // Generate schema markup if template is provided
  const schemaJsonLd = template ? generateSchema(seoData, template) : null;

  return {
    ...seoData,
    schemaJsonLd,
  };
};

export default useSeo;

const query = graphql`
  query SEO {
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;